
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { customerReviews } from '../../../mocks/customerReviews';

export default function ReviewsSection() {
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    route: '',
    rating: 5,
    comment: '',
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.comment.trim() || !formData.name.trim() || !formData.email.trim()) return;
    if (formData.comment.length > 500) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('route', formData.route);
      body.append('rating', formData.rating.toString());
      body.append('comment', formData.comment);

      const response = await fetch('https://readdy.ai/api/form/d649rjdsi3r5mpi9v9hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ route: '', rating: 5, comment: '', name: '', email: '' });
        setTimeout(() => {
          setSubmitted(false);
          setShowForm(false);
        }, 4000);
      }
    } catch {
      // silently fail
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="avis" className="scroll-mt-28">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-night-blue flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-voyage-gold/10 rounded-xl">
            <i className="ri-star-smile-line text-voyage-gold text-xl"></i>
          </div>
          Avis clients avec photos
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-night-blue text-white rounded-full font-inter font-bold text-sm hover:bg-slate-800 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i>
          {showForm ? 'Fermer' : 'Partager mon expérience'}
        </button>
      </div>

      {/* Review Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 overflow-hidden"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-emerald-600 text-3xl"></i>
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-night-blue mb-2">Merci pour votre avis !</h3>
                  <p className="text-slate-500 font-inter">Votre témoignage sera publié après vérification.</p>
                </motion.div>
              ) : (
                <form id="review-form" onSubmit={handleSubmit} data-readdy-form>
                  <h3 className="font-playfair text-xl font-bold text-night-blue mb-6">Partagez votre expérience</h3>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-night-blue font-inter font-medium text-sm mb-2">Nom *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-voyage-gold transition-all"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label className="block text-night-blue font-inter font-medium text-sm mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-voyage-gold transition-all"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-night-blue font-inter font-medium text-sm mb-2">Trajet effectué</label>
                      <select
                        name="route"
                        value={formData.route}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-voyage-gold transition-all cursor-pointer bg-white"
                      >
                        <option value="">Sélectionnez un trajet</option>
                        <option value="Genève → Courchevel">Genève → Courchevel</option>
                        <option value="Genève → Chamonix">Genève → Chamonix</option>
                        <option value="Genève → Megève">Genève → Megève</option>
                        <option value="Genève → Verbier">Genève → Verbier</option>
                        <option value="Lyon → Val Thorens">Lyon → Val Thorens</option>
                        <option value="Lyon → Val d'Isère">Lyon → Val d&apos;Isère</option>
                        <option value="Lyon → Tignes">Lyon → Tignes</option>
                        <option value="Chambéry → Les Arcs">Chambéry → Les Arcs</option>
                        <option value="Chambéry → Méribel">Chambéry → Méribel</option>
                        <option value="Moûtiers → Méribel">Moûtiers → Méribel</option>
                        <option value="Autre trajet">Autre trajet</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-night-blue font-inter font-medium text-sm mb-2">Note</label>
                      <div className="flex items-center gap-1 py-3">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                            className="cursor-pointer p-1 transition-transform hover:scale-125"
                          >
                            <i className={`${star <= formData.rating ? 'ri-star-fill text-voyage-gold' : 'ri-star-line text-slate-300'} text-2xl`}></i>
                          </button>
                        ))}
                        <span className="ml-2 font-inter font-bold text-night-blue text-sm">{formData.rating}/5</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-night-blue font-inter font-medium text-sm mb-2">Votre avis *</label>
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-voyage-gold transition-all resize-none"
                      placeholder="Décrivez votre expérience avec Voyage Elite..."
                    />
                    <p className="text-slate-400 font-inter text-xs mt-1">{formData.comment.length}/500 caractères</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.comment.trim() || !formData.name.trim() || !formData.email.trim()}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-voyage-gold to-amber-500 text-night-blue rounded-full font-inter font-bold text-sm hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Publier mon avis
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews Grid */}
      <div className="grid sm:grid-cols-2 gap-6">
        {customerReviews.map((review, index) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden group hover:shadow-lg transition-all"
          >
            {/* Photo */}
            <div
              className="relative h-52 overflow-hidden cursor-pointer"
              onClick={() => setSelectedPhoto(review.id)}
            >
              <img
                src={review.photo}
                alt={`Transfert ${review.route} - Avis client Voyage Elite`}
                title={`${review.route} - ${review.name}`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

              {/* Rating badge */}
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <i className="ri-star-fill text-voyage-gold text-sm"></i>
                <span className="font-inter font-bold text-night-blue text-sm">{review.rating}/5</span>
              </div>

              {/* Date */}
              <div className="absolute bottom-3 right-3 text-white/80 font-inter text-xs">
                {new Date(review.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>

              {/* Zoom icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <i className="ri-zoom-in-line text-white text-2xl"></i>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="font-inter font-semibold text-night-blue text-sm">{review.route}</span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-500 font-inter text-xs">{review.vehicle}</span>
              </div>

              <blockquote className="text-slate-600 font-inter text-sm leading-relaxed mb-4 line-clamp-4">
                &ldquo;{review.comment}&rdquo;
              </blockquote>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-voyage-gold to-amber-500 rounded-full flex items-center justify-center text-white font-inter font-bold text-xs">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-inter font-semibold text-night-blue text-sm">{review.name}</div>
                    <div className="font-inter text-xs text-slate-400">Chauffeur : {review.chauffeur}</div>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className={`${i < review.rating ? 'ri-star-fill text-voyage-gold' : 'ri-star-line text-slate-300'} text-sm`}></i>
                  ))}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-1.5 text-emerald-600 font-inter text-xs font-medium">
                <i className="ri-checkbox-circle-fill"></i>
                Avis vérifié — Transfert confirmé
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedPhoto !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all cursor-pointer z-10"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {(() => {
              const review = customerReviews.find(r => r.id === selectedPhoto);
              if (!review) return null;
              return (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="max-w-4xl w-full"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="w-full h-[60vh] rounded-2xl overflow-hidden">
                    <img
                      src={review.photo}
                      alt={`Transfert ${review.route}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-4 text-center">
                    <p className="text-white font-playfair text-xl font-bold">{review.route}</p>
                    <p className="text-white/70 font-inter text-sm mt-1">Par {review.name} — {review.vehicle}</p>
                  </div>
                </motion.div>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
